/* eslint-disable react/no-danger */
import { BonusClaimRewardModalContentHeaderProps } from './props';
import styles from './styles.module.scss';
import useTranslation from 'next-translate/useTranslation';
import UserBonusCardIcon from '@/components/Bonus/UserBonus/Card/Icon';
import BonusClaimModalContentHeaderSkeleton from './Skeleton';
import { activeUserBonusStatus } from '@/constants/user-bonus';
import { I18n } from '@/services/I18n';
import { useLanguageDictionary } from '@/hooks/useLanguageDictionary';
import { useMemo } from 'react';
import { getBonusTypeColor } from '@/components/Bonus/UserBonus/Card/helper';
import { UserBonusCheckpointStatus } from '@/enums/userBonusCheckpointStatus';
import { useParsedBonusEvents } from '@/hooks/useParsedBonusEvents';
import { WalletService } from '@starsoft/common/services';
import { getMoneyOrAmount } from '@starsoft/common/utils';
import { Nullable } from '@starsoft/common/interfaces/nullable';
import { Money } from '@starsoft/common/models';

export default function BonusClaimModalContentHeader({
  bonus,
  isLoading,
}: BonusClaimRewardModalContentHeaderProps) {
  const { t } = useTranslation('bonus');
  const isActiveBonus: boolean = activeUserBonusStatus.includes(
    bonus?.status?.id?.toString(),
  );

  const descriptionI18n: I18n = useLanguageDictionary(
    bonus?.status?.description,
  );

  const totalRedeemedAmount: Nullable<Money> = useMemo(
    () =>
      bonus?.checkpoints
        ?.filter(
          checkpoint =>
            checkpoint?.status === UserBonusCheckpointStatus.Claimed,
        )
        ?.reduce((accumulator: Nullable<Money>, checkpoint) => {
          const amount = checkpoint?.amount?.copyWith({
            options: {
              scale: bonus?.coin?.decimals ?? 2,
            },
          });

          if (accumulator === null) {
            return amount;
          }

          return accumulator.add(amount?.toNumber());
        }, null),
    [bonus],
  );

  const events: string = useParsedBonusEvents(bonus?.bonus?.events);

  if (isLoading) {
    return <BonusClaimModalContentHeaderSkeleton />;
  }

  return (
    <div
      className={styles.card}
      style={{
        //@ts-ignore
        '--bonus-color': getBonusTypeColor(bonus?.bonus?.type?.id),
      }}
    >
      <div className={styles.card__column}>
        <div
          className={styles.card__label}
          dangerouslySetInnerHTML={{
            __html: `<div>${descriptionI18n
              .t({
                events: `<strong>${events}</strong>`,
                percentage: `<strong>${bonus?.bonus?.percentage?.toNumber()}%</strong>`,
                amount: `<strong>${getMoneyOrAmount(bonus?.amount)}</strong>`,
                rollover: `<strong>${bonus?.rollover?.toNumber()}</strong>`,
                coin: `<strong>${bonus?.coin?.code}</strong>`,
              })
              ?.replaceAll('%', `<strong>%</strong>`)}</div>`,
          }}
        />
        {isActiveBonus && (
          <div className={styles.card__column__wrapper}>
            <p className={styles.card__description}>{t('total_bonus')}</p>
            <div className={styles.card__column__value}>
              {WalletService.maskCurrency({
                amount: getMoneyOrAmount(totalRedeemedAmount),
                coin: bonus?.coin,
              })}
            </div>
          </div>
        )}
      </div>
      <UserBonusCardIcon events={bonus?.bonus?.events} />
    </div>
  );
}
